import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AssistantAccessArea,
  WorkspaceAssistantConfiguration,
  WorkspaceOwnerAssistantConfigurationUpdate,
} from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import { useSaveWorkspaceAssistantConfigurationMutation } from '@/hooks/useAssistantConfiguration';
import { AccessAreaList } from './AccessAreaList';
import { BehaviourFields } from './BehaviourFields';
import { ConfigurationCard } from './ConfigurationCard';
import { InstructionsField } from './InstructionsField';
import { ProactiveFeaturesField } from './ProactiveFeaturesField';
import { SaveBar } from './SaveBar';
import { toggleAccessArea } from './assistantConfigurationOptions';
import { normaliseInstructions } from './normaliseInstructions';
import { useConfigurationSaveNotifications } from './useConfigurationSaveNotifications';
import { useSyncedDraft } from './useSyncedDraft';

interface WorkspaceAssistantConfigurationFormProps {
  configuration: WorkspaceAssistantConfiguration;
}

function readSavedUpdate(
  configuration: WorkspaceAssistantConfiguration
): WorkspaceOwnerAssistantConfigurationUpdate {
  return {
    tone: configuration.workspace.tone,
    replyLength: configuration.workspace.replyLength,
    extraInstructions: configuration.workspace.extraInstructions,
    disabledAccessAreas: configuration.workspace.disabledAccessAreas,
    proactiveFeaturesEnabled: configuration.workspace.proactiveFeaturesEnabled,
  };
}

export function WorkspaceAssistantConfigurationForm({
  configuration,
}: WorkspaceAssistantConfigurationFormProps) {
  const { t } = useTranslation('assistant');
  const saveMutation = useSaveWorkspaceAssistantConfigurationMutation();
  const notifications = useConfigurationSaveNotifications();
  const savedUpdate = readSavedUpdate(configuration);
  const { draft, setDraft, baseRevision, adoptSaved } = useSyncedDraft(
    savedUpdate,
    configuration.workspaceRevision
  );
  const instructions = draft.extraInstructions === null ? '' : draft.extraInstructions;
  const nextUpdate: WorkspaceOwnerAssistantConfigurationUpdate = {
    ...draft,
    extraInstructions: normaliseInstructions(instructions),
  };
  const hasChanges = JSON.stringify(nextUpdate) !== JSON.stringify(savedUpdate);
  const allowedAreas = configuration.effective.allowedAccessAreas;

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    saveMutation.mutate(
      { value: nextUpdate, expectedRevision: baseRevision },
      {
        onSuccess: function adoptAndNotify(saved) {
          adoptSaved(readSavedUpdate(saved), saved.workspaceRevision);
          notifications.onSuccess();
        },
        onError: notifications.onError,
      }
    );
  }

  function isAreaAllowed(area: AssistantAccessArea): boolean {
    return allowedAreas.includes(area);
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <ConfigurationCard title={t('configuration.behaviourTitle')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BehaviourFields
            tone={draft.tone}
            replyLength={draft.replyLength}
            inheritedTone={configuration.platform.tone}
            inheritedReplyLength={configuration.platform.replyLength}
            onToneChange={function updateTone(nextTone) {
              setDraft({ ...draft, tone: nextTone });
            }}
            onReplyLengthChange={function updateReplyLength(nextReplyLength) {
              setDraft({ ...draft, replyLength: nextReplyLength });
            }}
          />
          <InstructionsField
            fieldId="workspace-assistant-instructions"
            value={instructions}
            hint={t('configuration.instructions.workspaceHint')}
            onChange={function updateInstructions(nextInstructions) {
              setDraft({ ...draft, extraInstructions: nextInstructions });
            }}
          />
        </div>
      </ConfigurationCard>

      <ConfigurationCard
        title={t('configuration.areas.title')}
        hint={t('configuration.areas.hint')}
      >
        <AccessAreaList
          resolveRowState={function resolveRowState(area) {
            const isAllowed = isAreaAllowed(area);
            return {
              isOn: isAllowed && draft.disabledAccessAreas.includes(area) === false,
              isLocked: isAllowed === false,
              note: isAllowed ? null : t('configuration.areas.lockedByPlatform'),
              noteActionLabel: null,
            };
          }}
          onToggle={function toggleArea(area) {
            setDraft({
              ...draft,
              disabledAccessAreas: toggleAccessArea(draft.disabledAccessAreas, area),
            });
          }}
        />
      </ConfigurationCard>

      <ConfigurationCard
        title={t('configuration.proactive.title')}
        hint={t('configuration.proactive.hint')}
      >
        <ProactiveFeaturesField
          value={draft.proactiveFeaturesEnabled}
          inheritedValue={configuration.platform.proactiveFeaturesEnabled}
          onChange={function selectProactive(nextValue) {
            setDraft({ ...draft, proactiveFeaturesEnabled: nextValue });
          }}
        />
      </ConfigurationCard>

      <SaveBar hasChanges={hasChanges} isSaving={saveMutation.isPending} />
    </form>
  );
}
